// src/services/TicketService.ts
import { supabase } from './supabaseClient';

export interface Ticket {
  id?: string;
  user_id?: string;
  subject: string;
  description: string;
  category: 'reservation' | 'billing' | 'technical' | 'general' | 'complaint';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assigned_to?: string;
  created_at?: string;
  updated_at?: string;
  resolved_at?: string;

  // ✅ non-breaking (optionnel) — utile si tu veux afficher l’IA plus tard
  ai_reply?: string;
  ai_status?: 'pending' | 'processing' | 'answered' | 'error';
}

export interface Message {
  id?: string;
  ticket_id: string;
  sender_id: string;
  sender_type: 'user' | 'agent' | 'system';
  content: string;
  attachments?: string[];
  read: boolean;
  created_at?: string;
}

type LocalTicket = Ticket & { id: string };

function envStr(key: string): string | undefined {
  return (import.meta as any)?.env?.[key] as string | undefined;
}

function envBool(key: string, def: boolean): boolean {
  const v = envStr(key);
  if (v == null) return def;
  return ['1', 'true', 'yes', 'on'].includes(String(v).toLowerCase());
}

function getHttpApiBase(): string {
  const base = envStr('VITE_HTTP_API_BASE');
  return base ? base.replace(/\/$/, '') : '';
}

class TicketService {
  private localKey = 'forfeo_anonymous_tickets_v1';

  private isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private getMontrealNow(): Date {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'America/Montreal' }));
  }

  private async getUserId(): Promise<string | null> {
    try {
      const { data } = await supabase.auth.getUser();
      return data?.user?.id || null;
    } catch {
      return null;
    }
  }

  // ---------- Fallback localStorage (SAFE) ----------
  private readLocalTickets(): LocalTicket[] {
    if (!this.isBrowser()) return [];
    try {
      const raw = window.localStorage.getItem(this.localKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writeLocalTickets(tickets: LocalTicket[]) {
    if (!this.isBrowser()) return;
    try {
      window.localStorage.setItem(this.localKey, JSON.stringify(tickets));
    } catch {
      // no-op
    }
  }

  private generateLocalId(): string {
    return `local_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }

  // ✅ Notifier l'IA (fire-and-forget, non-breaking)
  private async notifyAI(params: { ticketId?: string; ticket?: Partial<Ticket> }) {
    try {
      const base = getHttpApiBase();
      const url = base ? `${base}/api/support/tickets/ai` : `/api/support/tickets/ai`;

      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // on envoie ticketId + un fallback ticket (au cas où DB non accessible côté backend)
        body: JSON.stringify({
          ticketId: params.ticketId,
          ticket: params.ticket,
          lang: 'fr',
        }),
      }).catch(() => {});
    } catch {
      // ignore
    }
  }

  // Créer un nouveau ticket
  async createTicket(
    ticketData: Omit<Ticket, 'id' | 'status' | 'created_at' | 'updated_at'>
  ): Promise<Ticket> {
    const userId = await this.getUserId();

    const ticket: Omit<Ticket, 'id'> = {
      ...ticketData,
      user_id: userId || undefined,
      status: 'open',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ai_status: 'pending',
    };

    // ✅ Si pas connecté, on sauvegarde en local plutôt que de casser le widget
    if (!userId) {
      const localTicket: LocalTicket = { ...ticket, id: this.generateLocalId() };
      const all = this.readLocalTickets();
      this.writeLocalTickets([localTicket, ...all]);

      // ✅ on notifie quand même l’IA (sans stockage supabase côté backend si pas de ticketId)
      // (tu pourras afficher la réponse plus tard si tu veux)
      this.notifyAI({ ticket: localTicket }).catch(() => {});

      return localTicket;
    }

    const { data, error } = await supabase
      .from('tickets')
      .insert([ticket])
      .select()
      .single();

    if (error) throw error;

    // ✅ IMPORTANT: avis à l’IA + stockage réponse (backend écrit dans ticket_messages)
    if (data?.id) {
      this.notifyAI({
        ticketId: data.id,
        ticket: {
          subject: data.subject,
          description: data.description,
          category: data.category,
          priority: data.priority,
          user_id: data.user_id,
        },
      }).catch(() => {});
    }

    return data;
  }

  // Obtenir tous les tickets de l'utilisateur
  async getUserTickets(): Promise<Ticket[]> {
    const userId = await this.getUserId();

    // ✅ Pas connecté → retourne tickets locaux
    if (!userId) {
      return this.readLocalTickets().sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
    }

    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Obtenir un ticket spécifique
  async getTicket(ticketId: string): Promise<Ticket> {
    // ✅ local fallback
    if (ticketId.startsWith('local_')) {
      const t = this.readLocalTickets().find(x => x.id === ticketId);
      if (!t) throw new Error('Ticket local introuvable');
      return t;
    }

    const { data, error } = await supabase
      .from('tickets')
      .select('*')
      .eq('id', ticketId)
      .single();

    if (error) throw error;
    return data;
  }

  // Mettre à jour un ticket
  async updateTicket(ticketId: string, updates: Partial<Ticket>): Promise<Ticket> {
    // ✅ local fallback
    if (ticketId.startsWith('local_')) {
      const all = this.readLocalTickets();
      const idx = all.findIndex(x => x.id === ticketId);
      if (idx === -1) throw new Error('Ticket local introuvable');

      const updated: LocalTicket = {
        ...all[idx],
        ...updates,
        updated_at: new Date().toISOString()
      } as LocalTicket;

      all[idx] = updated;
      this.writeLocalTickets(all);
      return updated;
    }

    const { data, error } = await supabase
      .from('tickets')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', ticketId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Ajouter un message à un ticket
  async addMessage(ticketId: string, message: Omit<Message, 'id' | 'created_at'>): Promise<Message> {
    // ⚠️ Messages en local non implémentés (mais on garde 100% de ce que tu avais: Supabase)
    if (ticketId.startsWith('local_')) {
      throw new Error('Messages non disponibles pour tickets anonymes');
    }

    const fullMessage = {
      ...message,
      created_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('ticket_messages')
      .insert([fullMessage])
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Obtenir les messages d'un ticket
  async getTicketMessages(ticketId: string): Promise<Message[]> {
    if (ticketId.startsWith('local_')) {
      return [];
    }

    const { data, error } = await supabase
      .from('ticket_messages')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Marquer les messages comme lus
  async markMessagesAsRead(ticketId: string, _userId: string): Promise<void> {
    if (ticketId.startsWith('local_')) return;

    const { error } = await supabase
      .from('ticket_messages')
      .update({ read: true })
      .eq('ticket_id', ticketId)
      .eq('sender_type', 'agent')
      .eq('read', false);

    if (error) throw error;
  }

  // Obtenir les statistiques des tickets
  async getTicketStats(userId: string): Promise<{
    total: number;
    open: number;
    in_progress: number;
    resolved: number;
  }> {
    const { data, error } = await supabase
      .from('tickets')
      .select('status')
      .eq('user_id', userId);

    if (error) throw error;

    const tickets = data || [];
    return {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      in_progress: tickets.filter(t => t.status === 'in_progress').length,
      resolved: tickets.filter(t => t.status === 'resolved').length
    };
  }

  // Vérifier les heures d'ouverture (9h-17h, Lundi-Vendredi, America/Montreal)
  // ✅ Respecte VITE_SUPPORT_ALWAYS_OPEN si tu l'actives
  isSupportOpen(): boolean {
    if (envBool('VITE_SUPPORT_ALWAYS_OPEN', false)) return true;

    const montrealTime = this.getMontrealNow();

    const day = montrealTime.getDay(); // 0 = Dimanche, 6 = Samedi
    const hour = montrealTime.getHours();

    // Lundi à Vendredi, 9h à 17h
    const isWeekday = day >= 1 && day <= 5;
    const isBusinessHours = hour >= 9 && hour < 17;

    return isWeekday && isBusinessHours;
  }

  // Obtenir le temps d'attente estimé
  getEstimatedWaitTime(): string {
    if (!this.isSupportOpen()) {
      return 'Réouverture demain à 9h';
    }

    // ✅ cohérent avec Montreal time
    const hour = this.getMontrealNow().getHours();

    if (hour < 10) {
      return 'Moins de 5 minutes';
    } else if (hour < 12) {
      return '5-10 minutes';
    } else if (hour < 14) {
      return '10-15 minutes';
    } else if (hour < 16) {
      return '15-20 minutes';
    } else {
      return '20-30 minutes';
    }
  }
}

export default new TicketService();
